import {Injectable} from '@angular/core';
import {AuthenticationService} from '@modules/authentication';
import {DynamicNavigationService} from '../../../../navigation/dynamic-navigation.service';
import {GroupService} from './group.service';
import {GroupsManagementService} from './groups-management.service';
import {CommunicationCenterService} from '@modules/communication-center';
import {GroupManagementAuthorizationService} from './group-management-authorization.service';
import {AuthorizationService} from '@modules/authorization';
import {LicensesService} from './licenses.service';
import {MatDialog} from '@angular/material/dialog';
import {GroupManagementConfigurationService} from './group-management-configuration.service';
import {LearnerService} from './learner.service';
import {WorkgroupService} from '../workgroup/workgroup.service';
import {TrainerService} from '../trainer/trainer.service';
import {InstitutionGroupService} from './institution-group.service';
import {Subject, combineLatest, Observable, of} from 'rxjs';
import {DataEntity, DataCollection} from 'octopus-connect';
import {ForbiddenPathInterface} from '../../../../guards/is-user-authorised-by-role.guard';
import {GlobalAsyncRules} from 'shared/models/';
import {map, takeUntil, take, tap} from 'rxjs/operators';
import {AsyncRules, SyncRules} from '../models/rules';
import {Group, Institution, InstitutionGroup, Learner, Workgroup, GroupDataCollection, GroupDataEntity, InstitutionDataEntity} from '../definitions';
import {Trainer} from '../models/trainer';
import {localizedDate} from 'shared/utils/datetime';
import {modulesSettings} from '../../../../settings';

@Injectable({
    providedIn: 'root'
})
export class GroupsManagementInitializerService {

    // Copied properties from GroupsManagementModule constructor logic
    private static isMenuSet = false;
    childrenMenu: Array<any> = [];
    private isUserAuthorisedByRoleGuardForbiddenPaths: ForbiddenPathInterface[] = [];
    private userData: DataEntity;
    private logout$ = new Subject<void>(); // Manages cleanup on logout

    constructor(
        // Restore all necessary injections
        private authService: AuthenticationService,
        private dynamicNavigation: DynamicNavigationService,
        private groupsService: GroupService, // Restored
        private groupsManagementService: GroupsManagementService, // Restored
        private communicationCenter: CommunicationCenterService,
        private groupManagementAuthorizationService: GroupManagementAuthorizationService,
        private authorization: AuthorizationService,
        private licensesService: LicensesService,
        private dialog: MatDialog,
        private config: GroupManagementConfigurationService,
        // Restore services related to initial data emission
        private learnerService: LearnerService,
        private workgroupService: WorkgroupService,
        private trainerService: TrainerService,
        private institutionGroupService: InstitutionGroupService
    ) {
    }

    initialize(): void {
        // Logic moved from GroupsManagementModule constructor
        this.groupManagementAuthorizationService.activeRulesOnStartup();

        this.communicationCenter
            .getRoom('authentication')
            .getSubject('userData')
            .subscribe((data: DataEntity) => {
                if (data) {
                    this.userData = data;
                    this.postAuthentication(); // Call the moved method
                    this.registerNotifications(); // Extracted for clarity
                } else {
                    this.postLogout(); // Call the moved method
                }
            });
    }

    private registerNotifications(): void {
        this.communicationCenter.getRoom('notifications')
            .getSubject('registerNotification')
            .next({
                name: 'Groupes',
                type: 'ADDED_IN_GROUP',
                icon: 'business',
                text: 'groups-management.groups_notifications_add_in',
                textTransform: (text: string, data: Object) => ({groupName: data['groupName']}),
                action: (data: Object) => ['groups', 'list', 'workgroups']
            });
        // ... (add other notification registrations here) ...
        this.communicationCenter.getRoom('notifications')
            .getSubject('registerNotification')
            .next({
                name: 'Groupes',
                type: 'REMOVED_FROM_GROUP',
                icon: 'business',
                text: 'groups-management.groups_notifications_remove',
                textTransform: (text: string, data: Object) => ({groupName: data['groupName']}),
                action: (data: Object) => ['groups', 'list', 'workgroups']
            });
        this.communicationCenter.getRoom('notifications')
            .getSubject('registerNotification')
            .next({
                name: 'Groupes',
                type: 'GROUP_NAME_CHANGED',
                icon: 'business',
                text: 'groups-management.groups_notifications_rename',
                textTransform: (text: string, data: Object) => ({oldGroupName: data['oldName'], newGroupName: data['newName']}),
                action: (data: Object) => ['groups', 'list', 'workgroups']
            });
        this.communicationCenter.getRoom('notifications')
            .getSubject('registerNotification')
            .next({
                name: 'Groupes',
                type: 'ADDED_IN_YOUR_GROUP',
                icon: 'business',
                text: 'groups-management.groups_notifications_comrade_added',
                textTransform: (text: string, data: Object) => ({studentName: data['studentName'], groupName: data['groupName']}),
                action: (data: Object) => ['groups', 'list', 'workgroups']
            });
    }

    // Method moved from GroupsManagementModule (lines 307-319)
    private postLogout(): void {
        this.logout$.next();
        this.logout$.complete();
        this.logout$ = new Subject<void>();

        GroupsManagementInitializerService.isMenuSet = false; // Using static property here
        this.dynamicNavigation.clearMenuItem('level0', 'parameter');
        this.dynamicNavigation.clearMenuItem('level0', 'institution');
        this.dynamicNavigation.clearMenuItem('level0', 'param-child-learners');
        // Ensure isUserAuthorisedByRoleGuardForbiddenPaths is cleared or managed correctly
        if (this.isUserAuthorisedByRoleGuardForbiddenPaths.length > 0) {
            this.communicationCenter.getRoom('IsUserAuthorisedByRoleGuard').next('remove', this.isUserAuthorisedByRoleGuardForbiddenPaths);
            this.isUserAuthorisedByRoleGuardForbiddenPaths = []; // Clear local cache
        }
        this.communicationCenter.getRoom('global-authorization')
            .next(GlobalAsyncRules.accessToExternalLinkButton, {id: 'groups-management', shouldDisplay: null});
    }

    // Method moved from GroupsManagementModule (lines 321-327)
    private getForbiddenPathObject(accessLevel: string): ForbiddenPathInterface[] {
        // Use config service directly instead of groupsManagementService
        const forbiddenPathsSettings = this.config.settings.forbiddenPaths || {}; // Get settings from config service
        const forbiddenPaths = this.authService.getRoleAssociatedValueOrDefault(forbiddenPathsSettings);
        // Ensure forbiddenPaths is an array before mapping
        return Array.isArray(forbiddenPaths) ? forbiddenPaths.map((fp, index) => ({
            identifier: `groups_management_${accessLevel}_${index}`,
            path: fp
        })) : [];
    }

    // Method moved from GroupsManagementModule (lines 329-477)
    private postAuthentication(): void {
        // Ensure licensesService and communicationCenter are available via injection
        const userInstitutionLicences$: Observable<{ license: { type: string } }[]> = this.communicationCenter
            .getRoom('groups-management')
            .getSubject('institutionList'); // Assuming institutionList is emitted elsewhere or by initial load
        const userLicences$ = this.licensesService.currentUserLicenses$; // Assuming this is populated by LicensesService itself

        combineLatest([userInstitutionLicences$, userLicences$])
            .pipe(
                takeUntil(this.logout$),
                map(([userInstitutionLicences, userLicences]) => {
                    // Ensure userInstitutionLicences and userLicences are arrays before mapping
                    const institutionLicences = Array.isArray(userInstitutionLicences) ? userInstitutionLicences.map((institutionLicence) => institutionLicence?.license?.type) : [];
                    const licences = Array.isArray(userLicences) ? userLicences.map((licence) => licence?.type) : [];
                    return [...institutionLicences, ...licences].filter(Boolean); // Filter out undefined/null types
                }),
                map(licences => licences.includes('Class') || licences.length === 0), // sans licence ou avec une licence classe
            ).subscribe((isAuthorize) => {
            this.communicationCenter.getRoom('global-authorization')
                .next(GlobalAsyncRules.accessToExternalLinkButton, {id: 'groups-management', shouldDisplay: isAuthorize});
        });

        const isUserAuthorisedByRoleGuardForbiddenPaths = this.getForbiddenPathObject(this.authService.accessLevel);
        if (isUserAuthorisedByRoleGuardForbiddenPaths.length > 0) {
            this.isUserAuthorisedByRoleGuardForbiddenPaths = isUserAuthorisedByRoleGuardForbiddenPaths;
            this.communicationCenter.getRoom('IsUserAuthorisedByRoleGuard').next('add', isUserAuthorisedByRoleGuardForbiddenPaths);
        }

        this.childrenMenu = [];


        // Use config service directly for settingsAccess check
        if (this.authService.hasLevel(['trainer', 'manager', 'administrator']) &&
            this.config.settingsAccess.institutionsGroupAccess) {
            this.childrenMenu.push({
                'id': 'param-child', // Consider making IDs more unique if conflicts arise
                'title': 'Mes établissements',
                'translate': 'groups-management.institutions',
                'type': 'item',
                'url': '/groups/list/institutions', // Ensure routing works correctly with lazy loading
            });
        }

        // Use config service directly for isLinkTrainerMustBeDisplayed check
        if (this.authService.hasLevel(this.config.isLinkTrainerMustBeDisplayed())) {
            this.childrenMenu.push({
                'id': 'trainers',
                'title': 'Mes enseignants',
                'translate': 'groups-management.my_trainers',
                'type': 'item',
                'url': '/groups/list/trainers',
            });
        }

        if (this.authorization.currentUserCan(SyncRules.SeeAllGroup)
            || this.authorization.currentUserCan(SyncRules.SeeOwnGroup)) {
            this.childrenMenu.push(...[
                {
                    'id': 'param-child-groups',
                    'title': 'Mes classes',
                    'translate': 'groups-management.classes',
                    'type': 'item',
                    'url': '/groups/list/groups',
                }]);
        }

        if (this.authorization.currentUserCan(SyncRules.SeeAllGroup) || this.authorization.currentUserCan(SyncRules.SeeOwnGroup)) {
            this.childrenMenu.push(...[{
                'id': 'param-child-workgroups',
                'title': 'Mes groupes',
                'translate': 'groups-management.workgroups',
                'type': 'item',
                'url': '/groups/list/workgroups',
            }]);
        }

        if (this.authorization.currentUserCan<boolean>(SyncRules.SeeAllLearner)) {
            const item: { id: string, title: string, translate: string, type: string, url: string, icon?: string } = {
                'id': 'param-child-learners', // Made ID more specific
                'title': 'Mes élèves',
                'translate': 'groups-management.students',
                'type': 'item',
                'url': '/groups/list/learners',
            };
            if (this.config.isDisplayIconInMenuAllowed()) {
                item.icon = 'group';
            }
            this.childrenMenu.push(item);
        }

        if (this.authService.hasLevel(['learner'])) {
            // This logic might need adjustment depending on when learner data is available
            this.childrenMenu = [
                {
                    'id': 'param-child-groups-learner',
                    'title': 'Mes classes',
                    'translate': 'groups-management.classes',
                    'type': 'item',
                    'url': '/groups/list/groups',
                },
                {
                    'id': 'param-child-workgroups-learner',
                    'title': 'Mes groupes',
                    'translate': 'groups-management.workgroups',
                    'type': 'item',
                    'url': '/groups/list/workgroups',
                }];
        }

        const authorizedRoles = modulesSettings.groupsManagement.authorizedRoles;

        // Check if menu needs to be set and user has appropriate level
        if (!GroupsManagementInitializerService.isMenuSet && this.authService.hasLevel(authorizedRoles)) {
            combineLatest([this.authorization.currentUserCan<Observable<boolean>>(AsyncRules.AccessInstitutionUsersManagement),
                this.authorization.currentUserCan<Observable<boolean>>(AsyncRules.AccessInstitutionInformation)])
                .pipe(takeUntil(this.logout$)) // Ensure cleanup
                .subscribe(([hasAccessInstitutionUsersManagement, hasAccessInstitutionInformation]) => {
                    if (hasAccessInstitutionUsersManagement || hasAccessInstitutionInformation) {

                        this.dynamicNavigation.refreshModuleMenu('level0', {
                            'id': 'institution',
                            'title': 'institution management',
                            'translate': 'navigation.institution-management',
                            'type': 'item',
                            'icon': 'institution',
                            'url': hasAccessInstitutionUsersManagement ? 'institution/multi/users' : 'institution/multi/information',
                        });
                    }

                });

            if (!this.authService.isGAR() || (this.authService.isGAR() && this.authService.isAtLeastTrainer())) {
                // Ensure childrenMenu is populated before adding it
                if (this.childrenMenu.length > 0) {
                    this.dynamicNavigation.refreshModuleMenu('level0', {
                        'id': 'parameter',
                        'title': 'Group management',
                        'translate': 'navigation.groups-management',
                        'type': 'collapse',
                        'icon': 'group',
                        'children': this.childrenMenu // Use the populated menu
                    });
                }
            }

            GroupsManagementInitializerService.isMenuSet = true;
        }

        if (this.authorization.currentUserCan(SyncRules.AccessTrainersAndDirectorsManagementPanel)) {
            this.dynamicNavigation.refreshModuleMenu('level0', {
                'id': 'trainers-and-directors-management-panel',
                'title': 'trainers and directors management panel',
                'translate': 'groups-management.trainers-and-directors-management-panel',
                'type': 'item',
                'icon': 'group',
                'url': 'license-management',
            });
        }
    }
}